import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { useTheme } from "../components/ThemeProvider";
import { emojiVisualProcedencia } from "../utils/procedenciaColor";
import usePermiso from "../hooks/usePermiso";

// `permId` referencia el nodo de permisosTree.js (tablas_auxiliares > cfg_ta_*)
// que gobierna ver/modificar/alta/baja de cada pestaña.
const TABLAS = [
  { slug: "tipo-inmueble", label: "Tipo inmueble", extras: ["familia", "ta"], permId: "cfg_ta_tipo_inmueble" },
  { slug: "procedencias",  label: "Procedencias",  extras: ["emoji"], permId: "cfg_ta_procedencias" },
  { slug: "cocina", label: "Cocina", extras: [], permId: "cfg_ta_cocina" },
  { slug: "vistas", label: "Vistas", extras: [], permId: "cfg_ta_vistas" },
  { slug: "calefaccion", label: "Calefaccion", extras: [], permId: "cfg_ta_calefaccion" },
  { slug: "orientacion", label: "Orientacion", extras: [], permId: "cfg_ta_orientacion" },
  { slug: "aire-acondicionado", label: "Aire acondicionado", extras: [], permId: "cfg_ta_aire" },
  { slug: "muebles", label: "Muebles", extras: [], permId: "cfg_ta_muebles" },
  { slug: "piscina", label: "Piscina", extras: [], permId: "cfg_ta_piscina" },
  { slug: "estado-inmueble", label: "Estado inmueble", extras: [], permId: "cfg_ta_estado_inmueble" },
  { slug: "suelos", label: "Suelos", extras: [], permId: "cfg_ta_suelos" },
  { slug: "carpinteria-interior", label: "Carpintería interior", extras: [], permId: "cfg_ta_carp_int" },
  { slug: "carpinteria-exterior", label: "Carpintería exterior", extras: [], permId: "cfg_ta_carp_ext" },
  { slug: "tipo-terreno", label: "Tipo terreno", extras: ["descripcion"], permId: "cfg_ta_tipo_terreno" },
  { slug: "zona", label: "Zona", extras: ["poblacion"], permId: "cfg_ta_zona" },
  { slug: "urbanizacion", label: "Urbanizaciones", extras: ["poblacion", "zona"], permId: "cfg_ta_urbanizacion" },
];

const EXTRA_LABELS = {
  poblacion: "Población",
  zona: "Zona",
  familia: "Familia",
  descripcion: "Descripción",
  ta: "Grupo TA",
  emoji: "Emoji",
};

const FAMILIAS = ["residencial", "comercial", "industrial", "terrenos", "otros"];

const IDIOMAS_DISPONIBLES = [
  { codigo: "es", nombre: "Español" },
  { codigo: "ca", nombre: "Catalán" },
  { codigo: "en", nombre: "Inglés" },
  { codigo: "de", nombre: "Alemán" },
  { codigo: "fr", nombre: "Francés" },
  { codigo: "nl", nombre: "Holandés" },
  { codigo: "it", nombre: "Italiano" },
  { codigo: "ru", nombre: "Ruso" },
  { codigo: "pl", nombre: "Polaco" },
];

const HORARIO_AGENTE_DEFAULT = {
  activo: false,
  dias_semana: [1, 2, 3, 4, 5],
  trabaja_sabados: false,
  jornada: "completa",
  manana_inicio: "09:30",
  manana_fin: "14:00",
  tarde_inicio: "16:30",
  tarde_fin: "19:00",
};

const DIAS_HORARIO = [
  { d: 1, label: "Lun" },
  { d: 2, label: "Mar" },
  { d: 3, label: "Mié" },
  { d: 4, label: "Jue" },
  { d: 5, label: "Vie" },
  { d: 6, label: "Sáb" },
];

function horarioFromItem(item) {
  const h = item?.horario && typeof item.horario === "object" ? item.horario : {};
  return {
    ...HORARIO_AGENTE_DEFAULT,
    ...h,
    dias_semana: Array.isArray(h.dias_semana) ? h.dias_semana : HORARIO_AGENTE_DEFAULT.dias_semana,
    activo: h.activo === true,
    trabaja_sabados: h.trabaja_sabados === true,
  };
}

export default function Configuracion() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const [searchParams, setSearchParams] = useSearchParams();
  const tablaSlug = searchParams.get("tabla") || TABLAS[0].slug;
  const esAgentes = tablaSlug === "agentes";
  const tablaActiva = esAgentes ? { slug: "agentes", label: "Agentes", extras: [] } : (TABLAS.find(t => t.slug === tablaSlug) || TABLAS[0]);
  const tablasOrdenadas = useMemo(
    () => [...TABLAS].sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" })),
    []
  );
  // Cada pestaña de tabla auxiliar tiene su propio permiso (cfg_ta_<slug>): un rol/usuario
  // puede ver, p. ej., Procedencias y Zona pero no Urbanizaciones.
  const tablasVisibles = useMemo(
    () => tablasOrdenadas.filter((t) => puede(t.permId, "ver")),
    [tablasOrdenadas, puede]
  );
  const puedeVerAgentes = puede("cfg_agentes", "ver");
  const puedeModificarAgentes = puede("cfg_agentes", "modificar");
  const puedeAltaAgentes = puede("cfg_agentes", "alta");
  const puedeBajaAgentes = puede("cfg_agentes", "baja");
  const puedeReordenarAgentes = puede("cfg_agentes_orden", "modificar");
  // Campos del formulario de agente (Nuevo/Editar), cada grupo con su propio permiso.
  const puedeVerAgentesDatos = puede("cfg_agentes_datos", "ver");
  const puedeModificarAgentesDatos = puede("cfg_agentes_datos", "modificar");
  const puedeVerAgentesIdiomas = puede("cfg_agentes_idiomas", "ver");
  const puedeModificarAgentesIdiomas = puede("cfg_agentes_idiomas", "modificar");
  const puedeVerAgentesMaxLeads = puede("cfg_agentes_max_leads", "ver");
  const puedeModificarAgentesMaxLeads = puede("cfg_agentes_max_leads", "modificar");
  const puedeVerAgentesHorario = puede("cfg_agentes_horario", "ver");
  const puedeModificarAgentesHorario = puede("cfg_agentes_horario", "modificar");
  // Capacidades de la pestaña de tabla auxiliar activa (no aplica en Agentes).
  const puedeVerTablaActiva = esAgentes ? puedeVerAgentes : puede(tablaActiva.permId, "ver");
  const puedeModificarTablaActiva = esAgentes ? puedeModificarAgentes : puede(tablaActiva.permId, "modificar");
  const puedeAltaTablaActiva = esAgentes ? puedeAltaAgentes : puede(tablaActiva.permId, "alta");
  const puedeBajaTablaActiva = esAgentes ? puedeBajaAgentes : puede(tablaActiva.permId, "baja");
  const puedeReordenarTablaActiva = esAgentes ? puedeReordenarAgentes : puedeModificarTablaActiva;

  const inp = {
    width: "100%",
    padding: "8px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1px solid ${c.border}`,
    background: c.bgInput,
    color: c.textPrimary,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };
  const lbl = { fontSize: 13, fontWeight: 500, color: isDark ? c.textSecondary : "#0f172a", marginBottom: 4 };
  /** Texto tabla: en claro negro para contraste */
  const tblHead = isDark ? c.textMuted : "#0f172a";
  const tblCell = isDark ? c.textMuted : "#0f172a";
  const rowLine = `1px solid ${c.border}`;
  const card = {
    background: c.bgCard,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    color: c.textPrimary,
  };
  const navBtn = (active) => ({
    textAlign: "left",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    width: "100%",
    transition: "background 0.15s",
    background: active ? (isDark ? "rgba(37,99,235,0.25)" : "#dbeafe") : "transparent",
    color: active ? (isDark ? "#93c5fd" : "#1d4ed8") : (isDark ? c.textMuted : "#0f172a"),
  });

  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: "", orden: 0, familia: "", descripcion: "", poblacion: "", zona: "", ta: "", emoji: "" });
  const [filtroFamilia, setFiltroFamilia] = useState("");
  const [filtroTA, setFiltroTA] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  // Campos específicos de agentes
  const [formAgente, setFormAgente] = useState({
    nombre: "",
    email: "",
    telefono: "",
    idiomas: ["es"],
    max_leads_activos: 50,
    ana_asignacion: "rotar",
    orden: 0,
    horario: { ...HORARIO_AGENTE_DEFAULT },
  });

  useEffect(() => {
    cargar();
    setMostrarForm(false);
    setEditando(null);
    setDragId(null);
    setDragOverId(null);
    setFiltroBusqueda("");
  }, [tablaSlug]);

  const cargar = async () => {
    setCargando(true);
    try {
      const endpoint = esAgentes ? "/agentes" : "/auxiliares/" + tablaActiva.slug;
      const res = await api.get(endpoint);
      setItems(res.data.data);
    } catch (err) {
      console.error("Error cargando:", err);
    } finally {
      setCargando(false);
    }
  };

  const itemsVisibles = useMemo(() => {
    const q = filtroBusqueda.trim().toLowerCase();
    return items.filter((item) => {
      if (!esAgentes && tablaActiva.slug === "tipo-inmueble") {
        if (filtroFamilia && item.familia !== filtroFamilia) return false;
        if (filtroTA && item.ta !== filtroTA) return false;
      }
      if (!q) return true;
      const haystack = [
        item.nombre,
        item.poblacion,
        item.zona,
        item.email,
        item.telefono,
        item.descripcion,
        item.familia,
        item.ta,
      ]
        .filter((v) => v != null && v !== "")
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, esAgentes, tablaActiva.slug, filtroFamilia, filtroTA, filtroBusqueda]);

  const persistirOrden = useCallback(async (lista) => {
    const conOrden = lista.map((item, i) => ({ ...item, orden: i + 1 }));
    setItems(conOrden);
    setGuardandoOrden(true);
    try {
      const endpoint = esAgentes
        ? "/agentes/reordenar"
        : `/auxiliares/${tablaActiva.slug}/reordenar`;
      await api.put(endpoint, { orden: conOrden.map((x) => x.id) });
    } catch (err) {
      console.error("Error guardando orden:", err);
      await dialogs.alert("No se pudo guardar el orden: " + (err.response?.data?.error || err.message));
      cargar();
    } finally {
      setGuardandoOrden(false);
    }
  }, [esAgentes, tablaActiva.slug]);

  const ordenarAlfabetico = async () => {
    if (!items.length || guardandoOrden || !puedeReordenarTablaActiva) return;
    if (!(await dialogs.confirm("¿Reordenar todos los elementos de A a Z por nombre?"))) return;
    const sorted = [...items].sort((a, b) =>
      String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" })
    );
    await persistirOrden(sorted);
  };

  const onDragStartRow = (e, id) => {
    if (!puedeReordenarTablaActiva) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(id)); } catch { /* ignore */ }
  };
  const onDragOverRow = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDropRow = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!fromId || fromId === targetId || guardandoOrden || !puedeReordenarTablaActiva) return;
    const fromIdx = items.findIndex((x) => x.id === fromId);
    const toIdx = items.findIndex((x) => x.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    await persistirOrden(next);
  };
  const onDragEndRow = () => {
    setDragId(null);
    setDragOverId(null);
  };

  const estiloFilaOrdenable = (item, i) => {
    const baseBg = i % 2 === 0 ? c.bgCard : (isDark ? "#1c2230" : "#f1f5f9");
    const over = dragOverId === item.id && dragId && dragId !== item.id;
    return {
      background: over ? (isDark ? "rgba(37,99,235,0.22)" : "#dbeafe") : baseBg,
      transition: "background 0.15s, opacity 0.15s",
      opacity: dragId === item.id ? 0.45 : 1,
      outline: over ? `2px solid ${isDark ? "#60a5fa" : "#2563eb"}` : "none",
      outlineOffset: -2,
      cursor: guardandoOrden ? "wait" : (puedeReordenarTablaActiva ? "grab" : "default"),
    };
  };

  const cambiarTabla = (slug) => {
    setSearchParams({ tabla: slug });
    setForm({ nombre: "", orden: 0, familia: "", descripcion: "", poblacion: "", zona: "", ta: "", emoji: "" });
    setFormAgente({
      nombre: "",
      email: "",
      telefono: "",
      idiomas: ["es"],
      max_leads_activos: 50,
      ana_asignacion: "rotar",
      orden: 0,
      horario: { ...HORARIO_AGENTE_DEFAULT },
    });
  };

  // Si la pestaña activa (tabla auxiliar o Agentes) queda oculta por permisos,
  // saltar automáticamente a la primera pestaña que sí sea visible para este rol/usuario.
  useEffect(() => {
    if (esAgentes) {
      if (puedeVerAgentes) return;
      if (tablasVisibles.length) cambiarTabla(tablasVisibles[0].slug);
      return;
    }
    if (tablasVisibles.length === 0) {
      if (puedeVerAgentes) cambiarTabla("agentes");
      return;
    }
    if (tablasVisibles.some((t) => t.slug === tablaSlug)) return;
    cambiarTabla(tablasVisibles[0].slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablasVisibles, esAgentes, tablaSlug, puedeVerAgentes]);

  const abrirNuevo = () => {
    if (!puedeAltaTablaActiva) return;
    setEditando(null);
    if (esAgentes) {
      setFormAgente({
        nombre: "",
        email: "",
        telefono: "",
        idiomas: ["es"],
        max_leads_activos: 50,
        ana_asignacion: "rotar",
        orden: items.length + 1,
        horario: { ...HORARIO_AGENTE_DEFAULT },
      });
    } else {
      setForm({
        nombre: "",
        orden: items.length + 1,
        familia: "",
        descripcion: "",
        poblacion: "",
        zona: "",
        ta: "",
        emoji: "",
      });
    }
    setMostrarForm(true);
  };

  const abrirEditar = (item) => {
    if (!puedeModificarTablaActiva) return;
    setEditando(item.id);
    if (esAgentes) {
      setFormAgente({
        nombre: item.nombre || "",
        email: item.email || "",
        telefono: item.telefono || "",
        idiomas: item.idiomas || ["es"],
        max_leads_activos:
          item.max_leads_activos === 0 || item.max_leads_activos === "0"
            ? 0
            : item.max_leads_activos || 50,
        ana_asignacion: item.ana_asignacion || "rotar",
        orden: item.orden || 0,
        horario: horarioFromItem(item),
      });
    } else {
      setForm({
        nombre: item.nombre || "",
        orden: item.orden || 0,
        familia: item.familia || "",
        descripcion: item.descripcion || "",
        poblacion: item.poblacion || "",
        zona: item.zona || "",
        ta: item.ta || "",
        emoji: item.emoji || "",
      });
    }
    setMostrarForm(true);
  };

  const guardar = async () => {
    if (editando ? !puedeModificarTablaActiva : !puedeAltaTablaActiva) return;
    try {
      if (esAgentes) {
        const maxLeadsParsed = parseInt(formAgente.max_leads_activos, 10);
        const datos = {
          ...formAgente,
          max_leads_activos: Number.isFinite(maxLeadsParsed) && maxLeadsParsed >= 0 ? maxLeadsParsed : 50,
          orden: parseInt(formAgente.orden, 10) || 0,
        };
        if (editando) {
          await api.patch("/agentes/" + editando, datos);
        } else {
          await api.post("/agentes", datos);
        }
      } else {
        var datos = { nombre: form.nombre, orden: parseInt(form.orden) || 0 };
        tablaActiva.extras.forEach(function(c) { datos[c] = form[c] || null; });
        if (editando) {
          await api.patch("/auxiliares/" + tablaActiva.slug + "/" + editando, datos);
        } else {
          await api.post("/auxiliares/" + tablaActiva.slug, datos);
        }
      }
      setMostrarForm(false);
      setEditando(null);
      cargar();
    } catch (err) {
      await dialogs.alert("Error al guardar: " + (err.response?.data?.error || err.message));
    }
  };

  const eliminar = async (id) => {
    if (!puedeBajaTablaActiva) return;
    if (!(await dialogs.confirm("Seguro que quieres desactivar este elemento?"))) return;
    try {
      const endpoint = esAgentes ? "/agentes/" + id : "/auxiliares/" + tablaActiva.slug + "/" + id;
      await api.delete(endpoint);
      cargar();
    } catch (err) {
      await dialogs.alert("Error al eliminar");
    }
  };

  const cambiar = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const cambiarAgente = (e) => {
    const { name, value } = e.target;
    setFormAgente((prev) => ({ ...prev, [name]: value }));
  };

  const toggleIdioma = (codigo) => {
    setFormAgente(prev => {
      const tiene = prev.idiomas.includes(codigo);
      // No permitir quitar todos los idiomas
      if (tiene && prev.idiomas.length === 1) return prev;
      return {
        ...prev,
        idiomas: tiene ? prev.idiomas.filter(i => i !== codigo) : [...prev.idiomas, codigo]
      };
    });
  };

  const setHorario = (patch) => {
    setFormAgente((prev) => ({
      ...prev,
      horario: { ...prev.horario, ...patch },
    }));
  };

  const toggleDiaHorario = (d) => {
    setFormAgente((prev) => {
      const dias = prev.horario?.dias_semana || [];
      const tiene = dias.includes(d);
      const next = tiene ? dias.filter((x) => x !== d) : [...dias, d].sort((a, b) => a - b);
      return {
        ...prev,
        horario: { ...prev.horario, dias_semana: next.length ? next : [d] },
      };
    });
  };

  // Formulario de agentes
  const renderFormAgentes = () => (
    <div style={{ ...card, padding: 24, marginBottom: 24 }}>
      <h3 style={{ fontWeight: 700, color: c.textPrimary, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>{editando ? "Editar" : "Nuevo"} Agente</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {puedeVerAgentesDatos && (
          <>
            <div className="flex flex-col gap-1">
              <label style={lbl}>Nombre *</label>
              <input
                name="nombre" value={formAgente.nombre} onChange={cambiarAgente} style={inp} placeholder="Nombre del agente"
                disabled={!puedeModificarAgentesDatos}
                title={!puedeModificarAgentesDatos ? "No tienes permiso para modificar estos datos" : undefined}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label style={lbl}>Email</label>
              <input
                name="email" value={formAgente.email} onChange={cambiarAgente} style={inp} placeholder="email@vitalcasa.com"
                disabled={!puedeModificarAgentesDatos}
                title={!puedeModificarAgentesDatos ? "No tienes permiso para modificar estos datos" : undefined}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label style={lbl}>Teléfono</label>
              <input
                name="telefono" value={formAgente.telefono} onChange={cambiarAgente} style={inp} placeholder="+34 600 000 000"
                disabled={!puedeModificarAgentesDatos}
                title={!puedeModificarAgentesDatos ? "No tienes permiso para modificar estos datos" : undefined}
              />
            </div>
          </>
        )}
        {puedeVerAgentesMaxLeads && (
          <div className="flex flex-col gap-1">
            <label style={lbl}>Máx. leads activos</label>
            <input
              name="max_leads_activos" type="number" value={formAgente.max_leads_activos} onChange={cambiarAgente} style={inp}
              disabled={!puedeModificarAgentesMaxLeads}
              title={!puedeModificarAgentesMaxLeads ? "No tienes permiso para modificar el máximo de leads activos" : undefined}
            />
          </div>
        )}
        {puedeVerAgentesMaxLeads && (
          <div className="flex flex-col gap-1">
            <label style={lbl}>Leads / citas de Ana</label>
            <select
              name="ana_asignacion"
              value={formAgente.ana_asignacion || "rotar"}
              onChange={cambiarAgente}
              style={inp}
              disabled={!puedeModificarAgentesMaxLeads}
              title={!puedeModificarAgentesMaxLeads ? "No tienes permiso para modificar la asignación de Ana" : undefined}
            >
              <option value="rotar">Rota en el pool comercial</option>
              <option value="solo_exclusivas">Solo sus exclusivas</option>
              <option value="nunca">Nunca (sí puede ser captador)</option>
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label style={lbl}>Orden</label>
          <input name="orden" type="number" value={formAgente.orden} onChange={cambiarAgente} style={inp} />
        </div>
      </div>
      {puedeVerAgentesIdiomas && (
        <div className="mt-4">
          <label style={{ ...lbl, display: "block", marginBottom: 8 }}>Idiomas que habla</label>
          <div className="flex flex-wrap gap-2">
            {IDIOMAS_DISPONIBLES.map(idioma => {
              const activo = formAgente.idiomas.includes(idioma.codigo);
              return (
                <button
                  key={idioma.codigo}
                  type="button"
                  disabled={!puedeModificarAgentesIdiomas}
                  title={!puedeModificarAgentesIdiomas ? "No tienes permiso para modificar los idiomas" : undefined}
                  onClick={() => puedeModificarAgentesIdiomas && toggleIdioma(idioma.codigo)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 9999,
                    fontSize: 14,
                    fontWeight: 500,
                    border: `1px solid ${activo ? (isDark ? "#3b82f6" : "#93c5fd") : c.border}`,
                    cursor: puedeModificarAgentesIdiomas ? "pointer" : "not-allowed",
                    fontFamily: "inherit",
                    background: activo ? (isDark ? "rgba(37,99,235,0.35)" : "#dbeafe") : c.bgPanel,
                    color: activo ? (isDark ? "#bfdbfe" : "#1d4ed8") : c.textMuted,
                    opacity: puedeModificarAgentesIdiomas ? 1 : 0.6,
                  }}
                >
                  {activo ? "✓ " : ""}{idioma.nombre}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {puedeVerAgentesHorario && (
        <div className="mt-6 pt-4" style={{ borderTop: `1px solid ${c.border}` }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Horario en agenda</p>
          <p style={{ fontSize: 12, color: c.textMuted, marginBottom: 12, lineHeight: 1.45 }}>
            Marca los días y la jornada habituales. La agenda mostrará automáticamente los huecos no disponibles
            (p. ej. solo mañanas o sin sábados). Si el sábado está en guardia, ese día sí cuenta como laborable.
          </p>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: puedeModificarAgentesHorario ? "pointer" : "not-allowed", fontSize: 13, color: c.textPrimary }}>
            <input
              type="checkbox"
              checked={formAgente.horario?.activo === true}
              onChange={(e) => setHorario({ activo: e.target.checked })}
              disabled={!puedeModificarAgentesHorario}
              title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined}
              style={{ width: 16, height: 16 }}
            />
            Usar horario personalizado en la agenda
          </label>
          {formAgente.horario?.activo && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ ...lbl, display: "block", marginBottom: 6 }}>Días laborables</label>
                <div className="flex flex-wrap gap-2">
                  {DIAS_HORARIO.map(({ d, label }) => {
                    const on = (formAgente.horario?.dias_semana || []).includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={!puedeModificarAgentesHorario}
                        title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined}
                        onClick={() => puedeModificarAgentesHorario && toggleDiaHorario(d)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          border: `1px solid ${on ? (isDark ? "#3b82f6" : "#93c5fd") : c.border}`,
                          cursor: puedeModificarAgentesHorario ? "pointer" : "not-allowed",
                          fontFamily: "inherit",
                          background: on ? (isDark ? "rgba(37,99,235,0.35)" : "#dbeafe") : c.bgPanel,
                          color: on ? (isDark ? "#bfdbfe" : "#1d4ed8") : c.textMuted,
                          opacity: puedeModificarAgentesHorario ? 1 : 0.6,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: puedeModificarAgentesHorario ? "pointer" : "not-allowed", fontSize: 13, color: c.textPrimary }}>
                <input
                  type="checkbox"
                  checked={formAgente.horario?.trabaja_sabados === true}
                  onChange={(e) => setHorario({ trabaja_sabados: e.target.checked })}
                  disabled={!puedeModificarAgentesHorario}
                  title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined}
                  style={{ width: 16, height: 16 }}
                />
                Trabaja sábados (salvo guardia, que también cuenta)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginBottom: 12 }}>
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Jornada</label>
                  <select
                    value={formAgente.horario?.jornada || "completa"}
                    onChange={(e) => setHorario({ jornada: e.target.value })}
                    disabled={!puedeModificarAgentesHorario}
                    title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined}
                    style={inp}
                  >
                    <option value="completa">Completa (mañana y tarde)</option>
                    <option value="solo_manana">Solo mañanas</option>
                    <option value="personalizada">Personalizada</option>
                  </select>
                </div>
              </div>
              {(formAgente.horario?.jornada === "personalizada" || formAgente.horario?.jornada === "completa" || formAgente.horario?.jornada === "solo_manana") && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex flex-col gap-1">
                    <label style={lbl}>Mañana desde</label>
                    <input type="time" value={formAgente.horario?.manana_inicio || "09:30"} onChange={(e) => setHorario({ manana_inicio: e.target.value })} style={inp} disabled={!puedeModificarAgentesHorario} title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label style={lbl}>Mañana hasta</label>
                    <input type="time" value={formAgente.horario?.manana_fin || "14:00"} onChange={(e) => setHorario({ manana_fin: e.target.value })} style={inp} disabled={!puedeModificarAgentesHorario} title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined} />
                  </div>
                  {formAgente.horario?.jornada !== "solo_manana" && (
                    <>
                      <div className="flex flex-col gap-1">
                        <label style={lbl}>Tarde desde</label>
                        <input type="time" value={formAgente.horario?.tarde_inicio || "16:30"} onChange={(e) => setHorario({ tarde_inicio: e.target.value })} style={inp} disabled={!puedeModificarAgentesHorario} title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label style={lbl}>Tarde hasta</label>
                        <input type="time" value={formAgente.horario?.tarde_fin || "19:00"} onChange={(e) => setHorario({ tarde_fin: e.target.value })} style={inp} disabled={!puedeModificarAgentesHorario} title={!puedeModificarAgentesHorario ? "No tienes permiso para modificar el horario" : undefined} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex gap-3 mt-4">
        <button type="button" onClick={guardar} style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", borderRadius: 8, fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit" }}>{editando ? "Guardar cambios" : "Crear"}</button>
        <button type="button" onClick={() => { setMostrarForm(false); setEditando(null); }} style={{ padding: "8px 16px", border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 14, fontWeight: 500, color: c.textMuted, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
      </div>
    </div>
  );

  // Tabla de agentes
  const renderTablaAgentes = () => (
    <div style={{ ...card, overflow: "hidden" }}>
      <table className="w-full" style={{ borderCollapse: "collapse" }}>
        <thead style={{ background: isDark ? "#1a1f2e" : "#e8edf5", borderBottom: `2px solid ${c.border}` }}>
          <tr>
            {["Orden", "Nombre", "Email", "Teléfono", "Horario", "Idiomas", "Máx. leads", "Ana", "Acciones"].map((h, idx) => (
              <th key={h} style={{ textAlign: idx === 8 ? "right" : "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itemsVisibles.map((item, i) => (
            <tr
              key={item.id}
              draggable={!guardandoOrden && puedeReordenarAgentes}
              onDragStart={(e) => onDragStartRow(e, item.id)}
              onDragOver={(e) => onDragOverRow(e, item.id)}
              onDrop={(e) => onDropRow(e, item.id)}
              onDragEnd={onDragEndRow}
              style={estiloFilaOrdenable(item, i)}
            >
              <td style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span title="Arrastra para reordenar" style={{ color: c.textMuted, userSelect: "none", fontSize: 16, lineHeight: 1 }}>⠿</span>
                  {item.orden}
                </span>
              </td>
              <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 600, color: tblCell, borderBottom: rowLine }}>{item.nombre}</td>
              <td style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>{item.email || "-"}</td>
              <td style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>{item.telefono || "-"}</td>
              <td style={{ padding: "12px 16px", fontSize: 13, color: tblCell, borderBottom: rowLine }}>
                {item.horario?.activo ? (
                  <span style={{ color: "#16a34a", fontWeight: 600 }}>
                    {item.horario.jornada === "solo_manana" ? "Solo mañana" : item.horario.jornada === "completa" ? "Completa" : "Custom"}
                    {!item.horario.trabaja_sabados ? " · sin sáb" : ""}
                  </span>
                ) : (
                  <span style={{ color: c.textMuted }}>Oficina</span>
                )}
              </td>
              <td style={{ padding: "12px 16px", borderBottom: rowLine }}>
                <div className="flex flex-wrap gap-1">
                  {(item.idiomas || []).map(cod => (
                    <span key={cod} style={{ padding: "2px 8px", background: isDark ? "rgba(37,99,235,0.35)" : "#dbeafe", color: isDark ? "#bfdbfe" : "#1d4ed8", borderRadius: 9999, fontSize: 12, fontWeight: 500 }}>
                      {cod.toUpperCase()}
                    </span>
                  ))}
                </div>
              </td>
              <td style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>{item.max_leads_activos}</td>
              <td style={{ padding: "12px 16px", fontSize: 13, color: tblCell, borderBottom: rowLine }}>
                {item.ana_asignacion === "nunca"
                  ? "Nunca"
                  : item.ana_asignacion === "solo_exclusivas"
                    ? "Solo excl."
                    : "Rota"}
              </td>
              <td style={{ padding: "12px 16px", textAlign: "right", borderBottom: rowLine }}>
                <button
                  type="button"
                  onClick={() => puedeModificarAgentes && abrirEditar(item)}
                  disabled={!puedeModificarAgentes}
                  title={!puedeModificarAgentes ? "No tienes permiso para modificar agentes" : undefined}
                  style={{ color: isDark ? "#60a5fa" : "#2563eb", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: puedeModificarAgentes ? "pointer" : "not-allowed", opacity: puedeModificarAgentes ? 1 : 0.5, marginRight: 12, fontFamily: "inherit" }}
                >Editar</button>
                {puedeBajaAgentes && (
                  <button type="button" onClick={() => eliminar(item.id)} style={{ color: "#f87171", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Eliminar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const selStyle = { ...inp, cursor: "pointer" };

  return (
    <div className="flex" style={{ minHeight: "calc(100vh - 49px)", background: c.bgApp, color: c.textPrimary }}>
      <nav style={{ width: 224, flexShrink: 0, background: c.bgPanel, borderRight: `1px solid ${c.border}`, padding: 16 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Tablas auxiliares</p>
        <div className="flex flex-col gap-1">
          {tablasVisibles.map(t => (
            <button key={t.slug} type="button" onClick={() => cambiarTabla(t.slug)} style={navBtn(tablaActiva.slug === t.slug)}
              onMouseEnter={(e) => { if (tablaActiva.slug !== t.slug) e.currentTarget.style.background = c.bgCardHover || (isDark ? "#252a38" : "#f1f5f9"); }}
              onMouseLeave={(e) => { if (tablaActiva.slug !== t.slug) e.currentTarget.style.background = "transparent"; }}>
              {t.label}
            </button>
          ))}
        </div>
        {puedeVerAgentes && (
          <div style={{ borderTop: `1px solid ${c.border}`, marginTop: 16, paddingTop: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Equipo</p>
            <button type="button" onClick={() => cambiarTabla("agentes")} style={navBtn(esAgentes)}
              onMouseEnter={(e) => { if (!esAgentes) e.currentTarget.style.background = c.bgCardHover || (isDark ? "#252a38" : "#f1f5f9"); }}
              onMouseLeave={(e) => { if (!esAgentes) e.currentTarget.style.background = "transparent"; }}>
              👤 Agentes
            </button>
          </div>
        )}
      </nav>
      <main style={{ flex: 1, padding: 24, minWidth: 0 }}>
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h2 style={{ fontWeight: 700, color: c.textPrimary, fontSize: 18, margin: 0 }}>{tablaActiva.label}</h2>
            <p style={{ fontSize: 12, color: isDark ? c.textMuted : "#334155", marginTop: 4 }}>
              {filtroBusqueda.trim()
                ? `${itemsVisibles.length} de ${items.length} elementos`
                : `${items.length} elementos`}
              {guardandoOrden ? " · Guardando orden…" : (puedeReordenarTablaActiva ? " · Arrastra ⠿ para reordenar" : "")}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="search"
              value={filtroBusqueda}
              onChange={(e) => setFiltroBusqueda(e.target.value)}
              placeholder={tablaActiva.slug === "urbanizacion" ? "Buscar urbanización…" : "Buscar…"}
              aria-label="Buscar"
              style={{
                ...inp,
                width: 220,
                margin: 0,
                padding: "8px 12px",
              }}
            />
            {puedeReordenarTablaActiva && (
              <button
                type="button"
                onClick={ordenarAlfabetico}
                disabled={!items.length || guardandoOrden}
                title="Ordenar de A a Z por nombre y guardar"
                style={{
                  padding: "8px 14px",
                  background: c.bgInput,
                  color: c.textPrimary,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  border: `1px solid ${c.border}`,
                  cursor: !items.length || guardandoOrden ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  opacity: !items.length || guardandoOrden ? 0.5 : 1,
                }}
              >
                A–Z
              </button>
            )}
            {puedeAltaTablaActiva && (
              <button type="button" onClick={abrirNuevo} style={{ padding: "8px 16px", background: isDark ? "#2563eb" : "#1d4ed8", color: "#fff", borderRadius: 8, fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Nuevo</button>
            )}
          </div>
        </div>

        {mostrarForm && (esAgentes ? renderFormAgentes() : (
          <div style={{ ...card, padding: 24, marginBottom: 24 }}>
            <h3 style={{ fontWeight: 700, color: c.textPrimary, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>{editando ? "Editar" : "Nuevo"} {tablaActiva.label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1">
                <label style={lbl}>Nombre *</label>
                <input name="nombre" value={form.nombre} onChange={cambiar} style={inp} />
              </div>
              <div className="flex flex-col gap-1">
                <label style={lbl}>Orden</label>
                <input name="orden" type="number" value={form.orden} onChange={cambiar} style={inp} />
              </div>
              {tablaActiva.extras.includes("emoji") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Emoji</label>
                  <input name="emoji" value={form.emoji} onChange={cambiar} style={inp} placeholder="📸" />
                </div>
              )}
              {tablaActiva.extras.includes("ta") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Grupo TA</label>
                  <select name="ta" value={form.ta} onChange={cambiar} style={selStyle}>
                    <option value="">Sin asignar</option>
                    <option value="TA1">TA1 — Residencial</option>
                    <option value="TA2">TA2 — Terrenos</option>
                    <option value="TA3">TA3 — Comercial/Industrial</option>
                    <option value="TA4">TA4 — Garaje/Trastero</option>
                    <option value="TA5">TA5 — Libre</option>
                  </select>
                </div>
              )}
              {tablaActiva.extras.includes("familia") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Familia</label>
                  <select name="familia" value={form.familia} onChange={cambiar} style={selStyle}>
                    <option value="">Seleccionar</option>
                    {FAMILIAS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                  </select>
                </div>
              )}
              {tablaActiva.extras.includes("descripcion") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Descripcion</label>
                  <input name="descripcion" value={form.descripcion} onChange={cambiar} style={inp} />
                </div>
              )}
              {tablaActiva.extras.includes("poblacion") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Poblacion</label>
                  <input name="poblacion" value={form.poblacion} onChange={cambiar} style={inp} />
                </div>
              )}
              {tablaActiva.extras.includes("zona") && (
                <div className="flex flex-col gap-1">
                  <label style={lbl}>Zona</label>
                  <input name="zona" value={form.zona} onChange={cambiar} style={inp} />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <button type="button" onClick={guardar} style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", borderRadius: 8, fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit" }}>{editando ? "Guardar cambios" : "Crear"}</button>
              <button type="button" onClick={() => { setMostrarForm(false); setEditando(null); }} style={{ padding: "8px 16px", border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 14, fontWeight: 500, color: c.textMuted, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
            </div>
          </div>
        ))}

        {tablaActiva.slug === "tipo-inmueble" && (
          <div className="flex gap-3 mb-4 flex-wrap">
            <select value={filtroFamilia} onChange={e => setFiltroFamilia(e.target.value)} style={selStyle}>
              <option value="">Todas las familias</option>
              {FAMILIAS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
            </select>
            <select value={filtroTA} onChange={e => setFiltroTA(e.target.value)} style={selStyle}>
              <option value="">Todos los TA</option>
              <option value="TA1">TA1 — Residencial</option>
              <option value="TA2">TA2 — Terrenos</option>
              <option value="TA3">TA3 — Comercial/Industrial</option>
              <option value="TA4">TA4 — Garaje/Trastero</option>
              <option value="TA5">TA5 — Libre</option>
            </select>
            {(filtroFamilia || filtroTA) && (
              <button type="button" onClick={() => { setFiltroFamilia(""); setFiltroTA(""); }}
                style={{ ...inp, cursor: "pointer", width: "auto", color: c.textMuted }}>
                ✕ Limpiar
              </button>
            )}
          </div>
        )}

        {!puedeVerTablaActiva ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: c.textMuted }}>Sin permiso para ver esta sección.</div>
        ) : cargando ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: c.textMuted }}>Cargando...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: c.textMuted }}>
            <p style={{ fontWeight: 600, color: c.textSecondary }}>No hay elementos</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Pulsa &quot;Nuevo&quot; para añadir el primero</p>
          </div>
        ) : itemsVisibles.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: c.textMuted }}>
            <p style={{ fontWeight: 600, color: c.textSecondary }}>Sin resultados</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Prueba otro término de búsqueda</p>
          </div>
        ) : esAgentes ? renderTablaAgentes() : (
          <div style={{ ...card, overflow: "hidden" }}>
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead style={{ background: isDark ? "#1a1f2e" : "#e8edf5", borderBottom: `2px solid ${c.border}` }}>
                <tr>
                  <th style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>Orden</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>Nombre</th>
                  <th style={{ textAlign: "right", padding: "12px 16px 12px 48px", minWidth: 120, fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>Propiedades</th>
                  {tablaActiva.extras.map((ex) => (
                    <th key={ex} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>{EXTRA_LABELS[ex] || ex}</th>
                  ))}
                  <th style={{ textAlign: "right", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: tblHead, textTransform: "uppercase" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {itemsVisibles.map((item, i) => (
                  <tr
                    key={item.id}
                    draggable={!guardandoOrden && puedeReordenarTablaActiva}
                    onDragStart={(e) => onDragStartRow(e, item.id)}
                    onDragOver={(e) => onDragOverRow(e, item.id)}
                    onDrop={(e) => onDropRow(e, item.id)}
                    onDragEnd={onDragEndRow}
                    style={estiloFilaOrdenable(item, i)}
                  >
                    <td style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span title="Arrastra para reordenar" style={{ color: c.textMuted, userSelect: "none", fontSize: 16, lineHeight: 1 }}>⠿</span>
                        {item.orden}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 600, color: tblCell, borderBottom: rowLine }}>{item.nombre}</td>
                    <td style={{ padding: "12px 16px 12px 48px", minWidth: 120, textAlign: "right", fontSize: 14, fontVariantNumeric: "tabular-nums", color: (item.propiedades_count ?? 0) === 0 ? c.textMuted : tblCell, borderBottom: rowLine }}>
                      {item.propiedades_count ?? 0}
                    </td>
                    {tablaActiva.extras.map((ex) => (
                      <td key={ex} style={{ padding: "12px 16px", fontSize: 14, color: tblCell, borderBottom: rowLine }}>
                        {ex === "emoji"
                          ? emojiVisualProcedencia(item.nombre, item.emoji)
                          : (item[ex] != null && item[ex] !== "" ? item[ex] : "—")}
                      </td>
                    ))}
                    <td style={{ padding: "12px 16px", textAlign: "right", borderBottom: rowLine }}>
                      <button
                        type="button"
                        onClick={() => puedeModificarTablaActiva && abrirEditar(item)}
                        disabled={!puedeModificarTablaActiva}
                        title={!puedeModificarTablaActiva ? "No tienes permiso para modificar esta tabla" : undefined}
                        style={{ color: isDark ? "#60a5fa" : "#2563eb", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: puedeModificarTablaActiva ? "pointer" : "not-allowed", opacity: puedeModificarTablaActiva ? 1 : 0.5, marginRight: 12, fontFamily: "inherit" }}
                      >Editar</button>
                      {puedeBajaTablaActiva && (
                        <button type="button" onClick={() => eliminar(item.id)} style={{ color: "#f87171", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Eliminar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}